import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiSearch, FiMenu, FiX,
  FiHome, FiShoppingCart, FiActivity,
  FiTrendingUp, FiChevronDown, FiEye,
  FiUser, FiLogOut, FiGrid, FiServer, FiVideo,
  FiCode, FiCreditCard, FiSettings,
  FiTool,
} from 'react-icons/fi';
import { FaDiscord, FaYoutube, FaTools, FaWhatsapp } from 'react-icons/fa';
import { BsDisplay, BsCameraVideo } from 'react-icons/bs';
import logo from '../../../assets/images/logo.png';
import noImage from '../../../assets/images/no_img.jpg';
import flagSA from '../../../assets/images/countries/السعوديه.png';
import EuFlagIcon from '../../shared/EuFlagIcon';
import { resolveAvatar } from '../../../utilities/avatar';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../../store/auth/authSlice';
import { recordVisit } from '../../../store/siteVisit/siteVisitSlice';
import { getSettings } from '../../../store/settings/settingsSlice';

/* ─────────────────────────────────────────
  SHELL
───────────────────────────────────────── */
const Shell = styled.div`
  display: flex;
  min-height: 100vh;
  background: #0B090B;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  direction: ${p => p.theme.direction || 'rtl'};
`;

/* ─────────────────────────────────────────
  OVERLAY (mobile)
───────────────────────────────────────── */
const Overlay = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: ${p => p.open ? 'block' : 'none'};
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    z-index: 999;
  }
`;

/* ─────────────────────────────────────────
  SIDEBAR
───────────────────────────────────────── */
const Sidebar = styled.aside`
  width: 210px;
  min-width: 210px;
  background: #1A1A1A;
  border-inline-end: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  height: 100vh;
  position: sticky;
  top: 0;
  overflow-y: auto;
  scrollbar-width: none;
  flex-shrink: 0;
  &::-webkit-scrollbar { display: none; }

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    inset-inline-start: 0;
    height: 100vh;
    z-index: 1000;
    transform: ${p => p.open
    ? 'translateX(0)'
    : p.theme.isRtl ? 'translateX(100%)' : 'translateX(-100%)'
  };
    transition: transform 0.28s ease;
  }
`;

const SidebarLogo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 14px;
  text-decoration: none;
  flex-shrink: 0;

  img {
    width: 46px;
    height: 46px;
    object-fit: contain;
    flex-shrink: 0;
    border-radius: 8px;
  }

  .logo-text {
    display: flex;
    flex-direction: column;
    .brand { color: #f1f5f9; font-size: 15px; font-weight: 700; line-height: 1.2; }
    .sub   { color: #6b7280; font-size: 10px; margin-top: 1px; }
  }
`;

const DiscordCta = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 10px 10px;
  padding: 10px 12px;
  background: #5865f2;
  border-radius: 8px;
  color: #fff;
  font-size: 12.5px;
  font-weight: 700;
  text-decoration: none;
  flex-shrink: 0;
  transition: filter 0.15s, transform 0.14s;
  &:hover { filter: brightness(1.1); transform: translateY(-1px); }
  svg { font-size: 16px; }
`;

const WhatsappCta = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 10px 10px;
  padding: 10px 12px;
  background: #25d366;
  border-radius: 8px;
  color: #fff;
  font-size: 12.5px;
  font-weight: 700;
  text-decoration: none;
  flex-shrink: 0;
  transition: filter 0.15s, transform 0.14s;
  &:hover { filter: brightness(1.1); transform: translateY(-1px); }
  svg { font-size: 16px; }
`;

const DiscordWidgetBox = styled.div`
  position: relative;
  margin: 0 10px 12px;
  padding: 12px 14px 10px;
  background: #141414;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 12px;
  flex-shrink: 0;

  .widget-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.6px;
    color: #6b7280;
  }

  .widget-server {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 6px 0;
  }

  .widget-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 9px;
    overflow: hidden;
    background: linear-gradient(135deg, #facc15, #eab308);
    color: #1a1305;
    font-size: 17px;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .widget-name strong {
    display: block;
    font-size: 13.5px;
    color: #f1f5f9;
    font-weight: 800;
    line-height: 1.3;
  }

  .widget-name span {
    display: block;
    font-size: 10.5px;
    color: #6b7280;
    line-height: 1.3;
  }

  .widget-online {
    margin-bottom: 12px;
    font-size: 12px;
    color: #9ca3af;
    display: flex;
    align-items: baseline;
    gap: 5px;

    .count {
      color: #facc15;
      font-weight: 800;
      font-size: 17px;
    }
  }

  .widget-join {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 9px 12px;
    background: transparent;
    border: 1.5px solid #facc15;
    border-radius: 8px;
    color: #facc15;
    font-size: 11.5px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    text-decoration: none;
    transition: background 0.15s, transform 0.14s;
    &:hover { background: rgba(250, 204, 21, 0.1); transform: translateY(-1px); }
    svg { font-size: 15px; }
  }

  .widget-members {
    margin-top: 10px;
    max-height: 150px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 9px;
    padding-inline-end: 10px;

    &::-webkit-scrollbar { width: 3px; }
    &::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.04); border-radius: 3px; }
    &::-webkit-scrollbar-thumb { background: #facc15; border-radius: 3px; }
  }

  .widget-member {
    display: flex;
    align-items: center;
    gap: 8px;

    .avatar {
      position: relative;
      width: 26px;
      height: 26px;
      flex-shrink: 0;

      img {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        object-fit: cover;
        display: block;
      }

      .status {
        position: absolute;
        bottom: -1px;
        inset-inline-end: -1px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        border: 2px solid #141414;
        background: #6b7280;
        &.online { background: #43b581; }
        &.idle { background: #faa61a; }
        &.dnd { background: #f04747; }
      }
    }

    .uname {
      flex: 1;
      font-size: 12px;
      color: #d1d5db;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .side-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      flex-shrink: 0;
      background: #4b5563;
      &.online { background: #43b581; }
      &.idle { background: #faa61a; }
      &.dnd { background: #f04747; }
    }
  }
`;

const NavSection = styled.nav`
  padding: 8px 10px;
  flex: 1;

  a {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    border-radius: 4px;
    text-decoration: none;
    color: #9ca3af;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 1px;
    transition: background 0.15s, color 0.15s;
    svg { font-size: 15px; flex-shrink: 0; color: #6b7280; transition: color 0.15s; }
    &:hover { background: rgba(255,255,255,0.05); color: #e2e8f0; svg { color: #a78bfa; } }
    &.active { background: #9333EA; color: #fff; svg { color: #fff; } }

    &.whatsapp-link,
    &.youtube-link {
      margin-top: 8px;
      padding: 7px 14px;
      border-radius: 10px;
      color: #fff;
      font-weight: 700;
      letter-spacing: 0.2px;
      box-shadow: 0 4px 14px -4px rgba(0, 0, 0, 0.45);
      transition: filter 0.15s, transform 0.14s, box-shadow 0.15s;

      svg {
        font-size: 17px;
        color: #fff;
        background: rgba(255, 255, 255, 0.18);
        padding: 4px;
        border-radius: 50%;
        box-sizing: content-box;
      }

      &:hover {
        filter: brightness(1.08);
        transform: translateY(-2px);
        box-shadow: 0 6px 18px -4px rgba(0, 0, 0, 0.55);
      }
    }

    &.whatsapp-link {
      background: linear-gradient(135deg, #2fe084, #1fa855);
      margin-top: 14px;
    }

    &.youtube-link {
      background: linear-gradient(135deg, #ff4d4d, #e60000);
    }
  }
`;

/* ─────────────────────────────────────────
  FLOATING TOOLS DOCK — fixed, vertically centered, own sidebar
───────────────────────────────────────── */
const FloatingTools = styled.div`
  position: fixed;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 900;
  display: flex;
  align-items: center;

  @media (max-width: 768px) { display: none; }
`;

const FloatingToolsToggle = styled.button`
  position: relative;
  z-index: 2;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-inline-start: none;
  border-radius: 0 12px 12px 0;
  background: #1A1A1A;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.35);
  transition: background 0.15s, color 0.15s, opacity 0.15s;

  &:hover { background: rgba(147, 51, 234, 0.14); color: #a78bfa; }
  &.open { opacity: 0; pointer-events: none; }
`;

const FloatingToolsMenu = styled.div`
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 0;
  transform: translateY(-50%) scale(${p => (p.open ? 1 : 0.94)});
  transform-origin: center left;
  width: 230px;
  opacity: ${p => (p.open ? 1 : 0)};
  visibility: ${p => (p.open ? 'visible' : 'hidden')};
  pointer-events: ${p => (p.open ? 'auto' : 'none')};
  background: #1A1A1A;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-inline-start: none;
  border-radius: 0 14px 14px 0;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
  overflow: hidden;
  transition: opacity 0.18s ease, transform 0.18s ease, visibility 0.18s;

  .tools-menu-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    background: rgba(147, 51, 234, 0.1);
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);

    .tools-menu-icon {
      width: 30px;
      height: 30px;
      flex-shrink: 0;
      border-radius: 9px;
      background: #9333EA;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 15px;
    }

    span {
      font-size: 13px;
      font-weight: 800;
      color: #e2e8f0;
    }

    .tools-menu-close {
      margin-inline-start: auto;
      width: 24px;
      height: 24px;
      flex-shrink: 0;
      border: none;
      border-radius: 7px;
      background: transparent;
      color: #9ca3af;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      cursor: pointer;
      transition: background 0.15s, color 0.15s;

      &:hover { background: rgba(255, 255, 255, 0.08); color: #e2e8f0; }
    }
  }

  .tools-menu-list {
    display: flex;
    flex-direction: column;
    padding: 6px;
  }

  a {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px;
    border-radius: 10px;
    text-decoration: none;
    transition: background 0.15s;

    .tools-item-icon {
      width: 34px;
      height: 34px;
      flex-shrink: 0;
      border-radius: 9px;
      background: rgba(147, 51, 234, 0.12);
      color: #a78bfa;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      transition: background 0.15s, color 0.15s;
    }

    .tools-item-text {
      display: flex;
      flex-direction: column;
      min-width: 0;

      strong {
        font-size: 12.5px;
        font-weight: 700;
        color: #e2e8f0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      span {
        font-size: 10.5px;
        color: #6b7280;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    &:hover { background: rgba(255, 255, 255, 0.06); }
    &:hover .tools-item-icon { background: rgba(147, 51, 234, 0.22); color: #c4b5fd; }

    &.active {
      background: #9333EA;
      .tools-item-icon { background: rgba(255, 255, 255, 0.18); color: #fff; }
      .tools-item-text strong { color: #fff; }
      .tools-item-text span { color: rgba(255, 255, 255, 0.75); }
    }
  }
`;

/* ─────────────────────────────────────────
  MAIN AREA
───────────────────────────────────────── */
const MainArea = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

/* ─────────────────────────────────────────
  TOPBAR
───────────────────────────────────────── */
const Topbar = styled.header`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  height: 54px;
  background: #111;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
  overflow: hidden;

  /* hamburger — mobile only */
  .hamburger {
    display: none;
    @media (max-width: 768px) {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 34px;
      height: 34px;
      border-radius: 8px;
      border: 1px solid rgba(255,255,255,0.08);
      background: #1A1A1A;
      color: #9ca3af;
      cursor: pointer;
      flex-shrink: 0;
      font-size: 17px;
      &:hover { color: #e2e8f0; }
    }
  }

  /* visit badge */
  .visit-badge {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 20px;
    background: rgba(147, 51, 234, 0.08);
    border: 1px solid rgba(147, 51, 234, 0.18);
    font-size: 11px;
    color: #a78bfa;
    font-weight: 600;
    white-space: nowrap;
    flex-shrink: 0;
    svg { font-size: 11px; opacity: 0.8; }
    .v-num { color: #c4b5fd; font-weight: 800; direction: ltr; }

    @media (max-width: 640px) { display: none; }
  }

  /* search — flex 1 */
  .search-box {
    flex: 1;
    min-width: 0;
    position: relative;
    display: flex;
    align-items: center;

    .s-icon {
      position: absolute;
      inset-inline-end: 11px;
      color: #374151;
      font-size: 13px;
      pointer-events: none;
    }

    input {
      width: 100%;
      background: #1A1A1A;
      border: 1px solid rgba(255, 255, 255, 0.06);
      border-radius: 8px;
      padding-block: 8px;
      padding-inline-start: 40px;
      padding-inline-end: 34px;
      color: #e2e8f0;
      font-size: 13px;
      outline: none;
      font-family: inherit;
      transition: border-color 0.2s, background 0.2s;
      &::placeholder { color: #2d3748; }
      &:focus { border-color: rgba(147, 51, 234, 0.45); background: #1d1d1d; }
    }

    kbd {
      position: absolute;
      inset-inline-start: 8px;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 4px;
      padding: 2px 6px;
      font-size: 10px;
      color: #374151;
      font-family: inherit;
      pointer-events: none;
      @media (max-width: 480px) { display: none; }
    }

    @media (max-width: 480px) {
      input { padding-inline-start: 30px; }
    }
  }

  /* action buttons group */
  .tb-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
  }

  .tb-btn {
    // width: 34px;
    height: 34px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.06);
    background: #1A1A1A;
    color: #4b5563;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    transition: all 0.15s;
    white-space: nowrap;
    gap: 4px;
    padding: 0 8px;
    &:hover {
      background: rgba(147, 51, 234, 0.12);
      border-color: rgba(147, 51, 234, 0.32);
      color: #a78bfa;
    }
    .lang-flag {
      width: 18px;
      height: 13px;
      object-fit: cover;
      border-radius: 2px;
      overflow: hidden;
      flex-shrink: 0;
    }

    .lang-text {
      font-size: 10px;
      font-weight: 800;
      letter-spacing: 0.04em;
      @media (max-width: 480px) { display: none; }
    }
  }

  .divider {
    width: 1px;
    height: 20px;
    background: rgba(255, 255, 255, 0.07);
    flex-shrink: 0;
    @media (max-width: 480px) { display: none; }
  }

  .user-chip {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 3px 8px 3px 6px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.06);
    background: #1A1A1A;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.15s;
    &:hover { border-color: rgba(147, 51, 234, 0.32); background: rgba(147, 51, 234, 0.07); }

    .user-avatar {
      width: 26px;
      height: 26px;
      border-radius: 7px;
      overflow: hidden;
      background: #2A2A2A;
      flex-shrink: 0;
      border: 1px solid rgba(147, 51, 234, 0.3);
      img { width: 100%; height: 100%; object-fit: cover; display: block; }
    }

    .user-info {
      display: flex;
      flex-direction: column;
      @media (max-width: 520px) { display: none; }
      .u-name { font-size: 11.5px; font-weight: 600; color: #e2e8f0; line-height: 1.2; }
      .u-role { font-size: 9px; color: #6b7280; }
    }

    svg { color: #4b5563; font-size: 12px; }
  }
`;

/* ─────────────────────────────────────────
  CONTENT AREA — scrollable
───────────────────────────────────────── */
const ContentScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
`;

/* ─────────────────────────────────────────
  LAYOUT COMPONENT
───────────────────────────────────────── */
const WebsiteLayout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(() => location.pathname.startsWith('/12x12-builder'));
  const auth = useSelector(state => state.auth);
  const visits = useSelector(state => state.siteVisit.count);
  const social = useSelector(state => state.settings.data);
  const [discordWidget, setDiscordWidget] = useState(null);
  const [discordIcon, setDiscordIcon] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
    setSidebarOpen(false);
  };

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === 'ar' ? 'en' : 'ar');
  };

  useEffect(() => {
    dispatch(recordVisit());
    dispatch(getSettings());
  }, [dispatch]);

  useEffect(() => {
    const guildId = social?.discordGuildId;
    if (!guildId) {
      setDiscordWidget(null);
      return;
    }
    let cancelled = false;
    fetch(`https://discord.com/api/guilds/${guildId}/widget.json`)
      .then(res => (res.ok ? res.json() : null))
      .then(data => { if (!cancelled) setDiscordWidget(data); })
      .catch(() => { if (!cancelled) setDiscordWidget(null); });
    return () => { cancelled = true; };
  }, [social?.discordGuildId]);

  useEffect(() => {
    const invite = discordWidget?.instant_invite || social?.discord;
    const code = invite?.split('/').filter(Boolean).pop();
    if (!code) {
      setDiscordIcon(null);
      return;
    }
    let cancelled = false;
    fetch(`https://discord.com/api/v10/invites/${code}`)
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (cancelled) return;
        const guild = data?.guild;
        if (guild?.icon) {
          const ext = guild.icon.startsWith('a_') ? 'gif' : 'png';
          setDiscordIcon(`https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.${ext}`);
        } else {
          setDiscordIcon(null);
        }
      })
      .catch(() => { if (!cancelled) setDiscordIcon(null); });
    return () => { cancelled = true; };
  }, [discordWidget?.instant_invite, social?.discord]);

  const handleNavClick = () => setSidebarOpen(false);

  return (
    <Shell>
      <Overlay open={sidebarOpen} onClick={() => setSidebarOpen(false)} />

      {/* ── FLOATING TOOLS DOCK ── */}
      <FloatingTools>
        <FloatingToolsToggle
          type="button"
          className={toolsOpen ? 'open' : ''}
          onClick={() => setToolsOpen(o => !o)}
          aria-expanded={toolsOpen}
          title={t('website.nav.tools_nav')}
        >
          <FaTools />
        </FloatingToolsToggle>
        <FloatingToolsMenu open={toolsOpen}>
          <div className="tools-menu-header">
            <span className="tools-menu-icon"><FiSettings /></span>
            <span>{t('website.nav.tools_nav')}</span>
            <button
              type="button"
              className="tools-menu-close"
              onClick={() => setToolsOpen(false)}
              aria-label={t('general.close')}
            >
              <FiX />
            </button>
          </div>
          <div className="tools-menu-list">
            <NavLink to="/12x12-builder">
              <span className="tools-item-icon"><FiGrid /></span>
              <span className="tools-item-text">
                <strong>{t('website.nav.pixel_builder_nav')}</strong>
                <span>{t('website.nav.pixel_builder_desc')}</span>
              </span>
            </NavLink>
            {/* <NavLink to="/youtube-tool">
              <span className="tools-item-icon"><FaYoutube /></span>
              <span className="tools-item-text">
                <strong>{t('website.nav.youtube_tool_nav')}</strong>
                <span>{t('website.nav.youtube_tool_desc')}</span>
              </span>
            </NavLink> */}
          </div>
        </FloatingToolsMenu>
      </FloatingTools>

      {/* ── SIDEBAR ── */}
      <Sidebar open={sidebarOpen}>
        <SidebarLogo to="/" onClick={handleNavClick}>
          <img src={logo} alt="logo" />
          <div className="logo-text">
            <span className="brand">{t('website.nav.brand')}</span>
            <span className="sub">{t('website.nav.brand_sub')}</span>
          </div>
        </SidebarLogo>

        <NavSection>
          <NavLink to="/" end onClick={handleNavClick}><FiHome /> {t('website.nav.home')}</NavLink>
          <NavLink to="/top-list" onClick={handleNavClick}><FiTrendingUp /> {t('website.nav.toplist_nav')}</NavLink>
          <NavLink to="/new-servers" onClick={handleNavClick}><BsDisplay /> {t('website.nav.new_servers_nav')}</NavLink>
          <NavLink to="/videos" onClick={handleNavClick}><BsCameraVideo /> {t('website.nav.videos_nav')}</NavLink>
          <NavLink to="/shop" onClick={handleNavClick}><FiShoppingCart /> {t('website.nav.shop_nav')}</NavLink>
          <NavLink to="/content-creators" onClick={handleNavClick}><FiActivity /> {t('website.nav.content_creators_nav')}</NavLink>
          <NavLink to="/programmers" onClick={handleNavClick}><FiCode /> {t('website.nav.programmers_nav')}</NavLink>
          <NavLink to="/services" onClick={handleNavClick}><FiActivity /> {t('website.nav.services_nav')}</NavLink>
          {social?.whatsapp && <a className="whatsapp-link" href={social.whatsapp} target="_blank" rel="noreferrer"><FaWhatsapp />  WhatsApp</a>}
          {social?.youtube && <a className="youtube-link" href={social.youtube} target="_blank" rel="noreferrer"><FaYoutube />   {t('website.nav.youtube_label')}</a>}
        </NavSection>
        {social?.discordGuildId && (
          <DiscordWidgetBox>
            <div className="widget-label">DISCORD</div>
            <div className="widget-server">
              <span className="widget-icon"><img src={discordIcon || logo} alt={t('website.nav.brand')} /></span>
              <div className="widget-name">
                <strong>{discordWidget?.name || t('website.nav.brand')}</strong>
                <span>{t('website.nav.brand_sub')}</span>
              </div>
            </div>
            {discordWidget && (
              <div className="widget-online">
                <span className="count">{discordWidget.presence_count}</span>
                {t('website.nav.discord_online_now')}
              </div>
            )}
            <a
              className="widget-join"
              href={discordWidget?.instant_invite || social.discord || '#'}
              target="_blank"
              rel="noreferrer"
            >
              <FaDiscord /> {t('website.nav.discord_cta')}
            </a>
            {discordWidget?.members?.length > 0 && (
              <div className="widget-members">
                {discordWidget.members.slice(0, 8).map(member => (
                  <div className="widget-member" key={member.id}>
                    <span className="avatar">
                      <img src={member.avatar_url} alt={member.username} />
                      <span className={`status ${member.status}`} />
                    </span>
                    <span className="uname">{member.username}</span>
                    <span className={`side-dot ${member.status}`} />
                  </div>
                ))}
              </div>
            )}
          </DiscordWidgetBox>
        )}



      </Sidebar>

      {/* ── MAIN ── */}
      <MainArea>
        <Topbar>
          {/* Hamburger */}
          <button className="hamburger" onClick={() => setSidebarOpen(o => !o)} aria-label="menu">
            {sidebarOpen ? <FiX /> : <FiMenu />}
          </button>

          {/* Visit badge */}
          <div className="visit-badge">
            <FiEye />
            {t('website.nav.visit_number')} <span className="v-num">#{visits}</span>
          </div>

          {/* Search */}
          <div className="search-box">
            <FiSearch className="s-icon" />
            <input type="text" placeholder={t('website.nav.search_placeholder')} />
            <kbd>⌘K</kbd>
          </div>

          {/* Actions */}
          <div className="tb-actions">
            {/* Language toggle */}
            <button className="tb-btn" onClick={toggleLang} title={t('website.nav.change_language')}>
              {i18n.language === 'ar'
                ? <EuFlagIcon className="lang-flag" />
                : <img className="lang-flag" src={flagSA} alt="عربي" />}
              <span className="lang-text">{i18n.language === 'ar' ? 'EN' : 'عربي'}</span>
            </button>

            {auth.isAuth && auth.user?.type === 'admin' && (
              <Link to="/admin" style={{ textDecoration: 'none' }}>
                <button className="tb-btn" title={t('website.nav.admin_panel')} style={{ color: '#a78bfa' }}>
                  <FiGrid />
                </button>
              </Link>
            )}

            <Link to="/wallet" style={{ textDecoration: 'none' }}>
              <button className="tb-btn" title={t('website.nav.wallet_nav')} style={{ color: '#a78bfa' }}>
                <FiCreditCard />
              </button>
            </Link>

            <div className="divider" />

            <Link to={auth.isAuth ? '/profile' : '/login'} className="user-chip">
              <div className="user-avatar">
                <img
                  src={auth.isAuth ? (resolveAvatar(auth.user) || noImage) : noImage}
                  alt="user"
                  onError={e => { e.currentTarget.src = noImage; }}
                />
              </div>
              <div className="user-info">
                <span className="u-name">
                  {auth.isAuth ? `${t('website.nav.hello')}${auth.user?.name}` : t('website.nav.sign_in')}
                </span>
                <span className="u-role">
                  {auth.isAuth ? (auth.user?.role?.name ?? auth.user?.type ?? t('website.nav.member')) : t('website.nav.guest')}
                </span>
              </div>
              {auth.isAuth ? <FiUser /> : <FiChevronDown />}
            </Link>

            {auth.isAuth && (
              <button
                className="tb-btn"
                title={t('website.nav.logout')}
                onClick={handleLogout}
                style={{ color: '#f87171' }}
              >
                <FiLogOut />
              </button>
            )}
          </div>
        </Topbar>

        {/* <iframe src="https://discord.com/widget?id=1524357833422798920&theme=dark" width="350" height="500" allowtransparency="true" frameborder="0" sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"></iframe> */}

        <ContentScroll>
          {children}
        </ContentScroll>
      </MainArea>
    </Shell>
  );
};

export default WebsiteLayout;
